import type { MsgId } from './i18n'

/** Which sections the dashboard shows and how; stored per browser. */

export type ChartKind = 'line' | 'area' | 'bar'

export type SectionId =
  | 'kpis'
  | 'timeseries'
  | 'seasonality'
  | 'weekdays'
  | 'heatmap'
  | 'events'
  | 'operational'
  | 'profiles'
  | 'yearly'

export type SectionConfig = {
  id: SectionId
  visible: boolean
  chartType: ChartKind
  /** false for sections that have a single fixed presentation */
  configurable: boolean
}

/** Message id of each section's name (used by the Customize dialog and as the card titles). */
export const SECTION_LABEL: Record<SectionId, MsgId> = {
  kpis: 'PROFILES_DASHBOARD.SECTION.KPIS',
  timeseries: 'PROFILES_DASHBOARD.SECTION.TIMESERIES',
  seasonality: 'PROFILES_DASHBOARD.SECTION.SEASONALITY',
  weekdays: 'PROFILES_DASHBOARD.SECTION.WEEKDAYS',
  heatmap: 'PROFILES_DASHBOARD.SECTION.HEATMAP',
  events: 'PROFILES_DASHBOARD.SECTION.EVENTS',
  operational: 'PROFILES_DASHBOARD.SECTION.OPERATIONAL',
  profiles: 'PROFILES_DASHBOARD.SECTION.PROFILES',
  yearly: 'PROFILES_DASHBOARD.SECTION.YEARLY',
}

export const DEFAULT_SECTIONS: SectionConfig[] = [
  { id: 'kpis', visible: true, chartType: 'bar', configurable: false },
  {
    id: 'timeseries',
    visible: true,
    chartType: 'line',
    configurable: true,
  },
  {
    id: 'seasonality',
    visible: true,
    chartType: 'line',
    configurable: true,
  },
  {
    id: 'weekdays',
    visible: true,
    chartType: 'bar',
    configurable: true,
  },
  {
    id: 'heatmap',
    visible: true,
    chartType: 'bar',
    configurable: false,
  },
  { id: 'events', visible: true, chartType: 'bar', configurable: true },
  {
    id: 'operational',
    visible: true,
    chartType: 'bar',
    configurable: true,
  },
  {
    id: 'profiles',
    visible: true,
    chartType: 'line',
    configurable: true,
  },
  { id: 'yearly', visible: true, chartType: 'bar', configurable: true },
]

/** v2: the first (mock) dashboard stored under v1 and had sections that no longer exist. */
export const STORAGE_KEY = 'profiles.dashboard.sections.v2'

const KINDS: ChartKind[] = ['line', 'area', 'bar']

export function loadSections(): SectionConfig[] {
  const defaults = DEFAULT_SECTIONS.map((s) => ({ ...s }))
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaults
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return defaults
    return defaults.map((d) => {
      const saved = parsed.find((s: any) => s && s.id === d.id)
      if (!saved) return d
      return {
        ...d,
        visible: typeof saved.visible === 'boolean' ? saved.visible : d.visible,
        chartType:
          d.configurable && KINDS.includes(saved.chartType) ? saved.chartType : d.chartType,
      }
    })
  } catch {
    return defaults
  }
}

export function saveSections(sections: SectionConfig[]): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(
        sections.map((s) => ({ id: s.id, visible: s.visible, chartType: s.chartType }))
      )
    )
  } catch {
    // storage unavailable (private mode / quota): the choice just is not remembered
  }
}
